import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const C = { primary: '#8B1A1A', gold: '#D4A017' };

function StarPicker({ value, onChange, readOnly = false, size = 20 }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span
          key={n}
          style={{
            fontSize: size,
            cursor: readOnly ? 'default' : 'pointer',
            color: n <= (hover || value) ? C.gold : '#ddd',
            transition: 'color 0.15s',
          }}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && onChange(n)}
        >★</span>
      ))}
    </div>
  );
}

function avgRating(reviews) {
  if (!reviews.length) return 0;
  return (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);
}

// Extracts the shortcode from any instagram.com/reel/... or instagram.com/p/... URL
function extractReelShortcode(url) {
  if (!url || !url.trim()) return null;
  const match = url.trim().match(/instagram\.com\/(?:[A-Za-z0-9_.]+\/)?(?:reel|p)\/([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
}

function InstagramReelEmbed({ shortcode, author }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 8,
          fontSize: 12,
          color: '#888',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E1306C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="1" fill="#E1306C" stroke="none" />
        </svg>
        Instagram Reel by {author}
      </div>
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 340,
          background: '#f5f5f5',
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid #e0d0d0',
        }}
      >
        {!loaded && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fafafa',
              color: '#aaa',
              fontSize: 13,
              zIndex: 1,
            }}
          >
            Loading reel…
          </div>
        )}
        <iframe
          src={`https://www.instagram.com/reel/${shortcode}/embed/`}
          width="340"
          height="500"
          frameBorder="0"
          scrolling="no"
          allowTransparency={true}
          allow="encrypted-media"
          title={`Instagram Reel by ${author}`}
          onLoad={() => setLoaded(true)}
          style={{ display: 'block', border: 'none' }}
        />
      </div>
    </div>
  );
}

export default function ReviewSection({ productId }) {
  const { user } = useAuth();
  const storageKey = `a1looks_reviews_${productId}`;
  const [reviews, setReviews] = useState(() => JSON.parse(localStorage.getItem(storageKey) || '[]'));
  const [form, setForm] = useState({ rating: 0, title: '', body: '', instagramUrl: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(reviews));
  }, [reviews, storageKey]);

  const alreadyReviewed = user && reviews.some(r => r.username === user.username);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.rating) { setError('Please select a star rating.'); return; }
    if (!form.title.trim()) { setError('Please add a review title.'); return; }

    const reelShortcode = extractReelShortcode(form.instagramUrl);
    if (form.instagramUrl.trim() && !reelShortcode) {
      setError('Please enter a valid Instagram Reel URL (e.g. https://www.instagram.com/reel/ABC123/).');
      return;
    }

    setError('');
    const newReview = {
      id: Date.now(),
      username: user.username,
      displayName: user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.username,
      rating: form.rating,
      title: form.title.trim(),
      body: form.body.trim(),
      reelShortcode: reelShortcode || null,
      date: new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }),
    };
    setReviews(prev => [newReview, ...prev]);
    setForm({ rating: 0, title: '', body: '', instagramUrl: '' });
    setSubmitted(true);
  };

  const avg = avgRating(reviews);
  const dist = [5, 4, 3, 2, 1].map(n => ({ n, count: reviews.filter(r => r.rating === n).length }));

  return (
    <section style={{ marginTop: 60, borderTop: '2px solid #f0e0e0', paddingTop: 40 }}>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#2C2C2C', marginBottom: 28 }}>
        Customer Reviews
      </h2>

      {reviews.length > 0 && (
        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', marginBottom: 36, padding: '20px 24px', background: '#FFF8F0', borderRadius: 8 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 52, fontWeight: 700, color: C.primary, lineHeight: 1 }}>{avg}</div>
            <StarPicker value={Math.round(avg)} readOnly size={18} />
            <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{reviews.length} review{reviews.length !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            {dist.map(({ n, count }) => (
              <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: '#666', width: 28 }}>{n}★</span>
                <div style={{ flex: 1, height: 8, background: '#e0d0d0', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: reviews.length ? `${(count / reviews.length) * 100}%` : '0%', height: '100%', background: C.gold, borderRadius: 4, transition: 'width 0.4s' }} />
                </div>
                <span style={{ fontSize: 12, color: '#888', width: 16 }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {user && !alreadyReviewed && !submitted && (
        <div style={{ background: '#fff', border: '1.5px solid #f0e0e0', borderRadius: 8, padding: '24px', marginBottom: 32 }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, marginBottom: 16 }}>Write a Review</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Your Rating *</label>
              <StarPicker value={form.rating} onChange={n => setForm(f => ({ ...f, rating: n }))} size={28} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Review Title *</label>
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Summarize your experience"
                maxLength={100}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0d0d0', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Review (optional)</label>
              <textarea
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Tell others about your experience with this product..."
                rows={3}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0d0d0', borderRadius: 4, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            {/* Instagram Reel field */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E1306C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.5" cy="6.5" r="1" fill="#E1306C" stroke="none" />
                  </svg>
                  Instagram Reel (optional)
                </span>
              </label>
              <input
                type="url"
                value={form.instagramUrl}
                onChange={e => setForm(f => ({ ...f, instagramUrl: e.target.value }))}
                placeholder="https://www.instagram.com/reel/..."
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0d0d0', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Share your reel wearing or using this product — it will play on this page
              </p>
            </div>

            {error && <p style={{ color: '#e53935', fontSize: 13, marginBottom: 10 }}>{error}</p>}
            <button type="submit" style={{ background: C.primary, color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Submit Review
            </button>
          </form>
        </div>
      )}

      {submitted && (
        <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '12px 16px', marginBottom: 24, color: '#2e7d32', fontSize: 14 }}>
          Thank you for your review!
        </div>
      )}

      {!user && (
        <p style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>
          <a href="/login" style={{ color: C.primary, textDecoration: 'none', fontWeight: 600 }}>Log in</a> to write a review.
        </p>
      )}

      {reviews.length === 0 ? (
        <p style={{ color: '#aaa', fontSize: 15 }}>No reviews yet. Be the first to review this product!</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {reviews.map(r => (
            <div key={r.id} style={{ borderBottom: '1px solid #f0e0e0', paddingBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div>
                  <StarPicker value={r.rating} readOnly size={16} />
                  <p style={{ fontWeight: 700, fontSize: 15, color: '#2C2C2C', marginTop: 4 }}>{r.title}</p>
                </div>
                <span style={{ fontSize: 12, color: '#aaa' }}>{r.date}</span>
              </div>
              {r.body && <p style={{ color: '#555', fontSize: 14, lineHeight: 1.7 }}>{r.body}</p>}
              <p style={{ fontSize: 12, color: '#aaa', marginTop: 6 }}>by {r.displayName}</p>
              {r.reelShortcode && (
                <InstagramReelEmbed shortcode={r.reelShortcode} author={r.displayName} />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
